// hooks/market/useShopsList.ts
//
// Récupère la liste paginée des boutiques publiées, pour
// app/(tabs)/market/boutiques.tsx. Appelle le vrai backend :
//   POST {API_BASE_URL}/market   body: { action: 'get-shops', access_token, limit, offset }
// (voir backend/handleMarket.ts)

import { useState, useCallback, useEffect, useRef } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';

// Miroir des colonnes retournées par market_public_shops_feed (voir
// backend/handleMarket.ts → getShops). Champs limités à ceux utilisés
// par l'écran liste ; le détail complet vit dans useShop.ts.
export type MarketShopListItem = {
  shop_id: string;
  name: string;
  slug: string;
  tagline: string | null;
  cover_image_url: string;
  country: string | null;
  city: string | null;
  is_verified: boolean;
  is_premium: boolean;
  published_at: string;
  total_products: number;
  subscriber_count: number;
};

const PAGE_SIZE = 20;

export function useShopsList() {
  const [shops, setShops]     = useState<MarketShopListItem[]>([]);
  const [loading, setLoading] = useState(true);       // chargement initial
  const [loadingMore, setLoadingMore] = useState(false); // pagination
  const [refreshing, setRefreshing]   = useState(false); // pull-to-refresh
  const [error, setError]     = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef   = useRef(0);
  const loadingRef  = useRef(false); // anti double-appel concurrent

  const fetchShops = useCallback(async (mode: 'initial' | 'more' | 'refresh') => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    if (mode === 'initial') setLoading(true);
    if (mode === 'more')    setLoadingMore(true);
    if (mode === 'refresh') setRefreshing(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const currentOffset = mode === 'more' ? offsetRef.current : 0;

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-shops',
          access_token: token,
          limit:        PAGE_SIZE,
          offset:       currentOffset,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de charger les boutiques');
        return;
      }

      const newShops: MarketShopListItem[] = data.shops || [];

      if (mode === 'more') {
        setShops(prev => {
          const existingIds = new Set(prev.map(s => s.shop_id));
          return [...prev, ...newShops.filter(s => !existingIds.has(s.shop_id))];
        });
      } else {
        setShops(newShops);
      }

      offsetRef.current = data.next_offset ?? (currentOffset + newShops.length);
      setHasMore(Boolean(data.has_more));
    } catch (err) {
      console.error('[useShopsList]', err);
      setError('network_error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setRefreshing(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchShops('initial');
  }, [fetchShops]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingRef.current) return;
    fetchShops('more');
  }, [hasMore, fetchShops]);

  const refresh = useCallback(() => {
    fetchShops('refresh');
  }, [fetchShops]);

  return { shops, loading, loadingMore, refreshing, error, hasMore, loadMore, refresh };
}
